"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { fetchOnboardStatus, OnboardApiError, submitPublicOnboard } from "@/shared/services/onboardApi";
import type {
    OnboardingUiPhase,
    OnboardStep,
    PublicOnboardRequestDto,
    PublicOnboardResponseDto,
} from "@/shared/types/onboard.types";
import { trackBeautyCampaignEvent } from "@/shared/utils/beautyCampaignAnalytics";

import { trackMarketing } from "@/components/marketing/launch";

const POLL_INTERVAL_MS = 5000;

function needsTelegramConfirmation(data: PublicOnboardResponseDto): boolean {
    return Boolean(data.telegramConfirmUrl) && data.telegramLinked !== true;
}

export function useOnboardPolling() {
    const [phase, setPhase] = useState<OnboardingUiPhase>("idle");
    const [steps, setSteps] = useState<OnboardStep[]>([]);
    const [adminUrl, setAdminUrl] = useState<string | null>(null);
    const [contactEmail, setContactEmail] = useState<string | null>(null);
    const [telegramConfirmUrl, setTelegramConfirmUrl] = useState<string | null>(null);
    const [telegramBotUsername, setTelegramBotUsername] = useState<string | null>(null);
    const [onboardMessage, setOnboardMessage] = useState<string | null>(null);
    const [telegramLinked, setTelegramLinked] = useState(false);
    const [campaignCode, setCampaignCode] = useState<string | null>(null);
    const [campaignFreeUntil, setCampaignFreeUntil] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const completedJobRef = useRef<string | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const clearPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const applyResponseMeta = useCallback((data: PublicOnboardResponseDto) => {
        setSteps(data.steps ?? []);
        setContactEmail(data.contactEmail ?? null);
        setTelegramConfirmUrl(data.telegramConfirmUrl ?? null);
        setTelegramBotUsername(data.telegramBotUsername ?? null);
        setOnboardMessage(data.message?.trim() ? data.message : null);
        setTelegramLinked(data.telegramLinked === true);
        setCampaignCode(data.campaignCode ?? null);
        setCampaignFreeUntil(data.campaignFreeUntil ?? null);
    }, []);

    const handleStatusUpdate = useCallback(
        (data: PublicOnboardResponseDto) => {
            applyResponseMeta(data);

            if (data.status === "COMPLETED") {
                if (completedJobRef.current !== data.jobId) {
                    completedJobRef.current = data.jobId;
                    trackMarketing("web_onboard_completed");
                }
                clearPolling();
                setPhase("success");
                setAdminUrl(data.adminUrl);
                if (data.campaignCode) {
                    trackBeautyCampaignEvent("beauty_campaign_signup_complete", null, {
                        campaign_code: data.campaignCode,
                    });
                    trackBeautyCampaignEvent("beauty_campaign_activation_success", null, {
                        campaign_code: data.campaignCode,
                    });
                } else if (data.campaignSkipReason) {
                    trackBeautyCampaignEvent("beauty_campaign_activation_rejected", null, {
                        reason: data.campaignSkipReason,
                    });
                }
                return;
            }

            if (data.status === "FAILED") {
                clearPolling();
                setPhase("error");
                setErrorMessage(data.error || "Произошла ошибка при развертывании системы.");
                return;
            }

            if (needsTelegramConfirmation(data)) {
                setPhase("telegram_confirm");
                return;
            }

            setPhase("provisioning");
        },
        [applyResponseMeta, clearPolling],
    );

    const startPolling = useCallback(
        (jobId: string) => {
            clearPolling();

            const poll = async () => {
                try {
                    const data = await fetchOnboardStatus(jobId);
                    handleStatusUpdate(data);
                } catch (error) {
                    if (error instanceof OnboardApiError && error.status === 429) {
                        return;
                    }
                    clearPolling();
                    setPhase("error");
                    setErrorMessage(
                        error instanceof OnboardApiError
                            ? error.message
                            : "Не удалось проверить статус развертывания. Попробуйте позже.",
                    );
                }
            };

            void poll();
            intervalRef.current = setInterval(() => {
                void poll();
            }, POLL_INTERVAL_MS);
        },
        [clearPolling, handleStatusUpdate],
    );

    const submitOnboard = useCallback(
        async (payload: PublicOnboardRequestDto) => {
            trackMarketing("web_onboard_submit", {industry:payload.industryPreset,plan:payload.planCode});
            setPhase("submitting");
            setErrorMessage(null);
            setSteps([]);
            setAdminUrl(null);
            setTelegramConfirmUrl(null);
            setTelegramBotUsername(null);
            setOnboardMessage(null);
            setTelegramLinked(false);
            setCampaignCode(null);
            setCampaignFreeUntil(null);

            try {
                const data = await submitPublicOnboard(payload);
                trackMarketing("web_onboard_accepted", {status:data.status});

                if (data.status === "COMPLETED") {
                    handleStatusUpdate(data);
                    return data;
                }

                if (data.status === "FAILED") {
                    applyResponseMeta(data);
                    setPhase("error");
                    setErrorMessage(data.error || "Не удалось запустить развертывание.");
                    return data;
                }

                handleStatusUpdate(data);
                startPolling(data.jobId);
                return data;
            } catch (error) {
                trackMarketing("web_onboard_error", {status:error instanceof OnboardApiError ? error.status : 0});
                setPhase("error");
                setErrorMessage(
                    error instanceof OnboardApiError
                        ? error.message
                        : "Не удалось отправить заявку на автоматический запуск.",
                );
                return null;
            }
        },
        [applyResponseMeta, handleStatusUpdate, startPolling],
    );

    const resetOnboard = useCallback(() => {
        clearPolling();
        setPhase("idle");
        setSteps([]);
        setAdminUrl(null);
        setContactEmail(null);
        setTelegramConfirmUrl(null);
        setTelegramBotUsername(null);
        setOnboardMessage(null);
        setTelegramLinked(false);
        setCampaignCode(null);
        setCampaignFreeUntil(null);
        setErrorMessage(null);
    }, [clearPolling]);

    useEffect(() => () => clearPolling(), [clearPolling]);

    return {
        phase,
        steps,
        adminUrl,
        contactEmail,
        telegramConfirmUrl,
        telegramBotUsername,
        onboardMessage,
        telegramLinked,
        campaignCode,
        campaignFreeUntil,
        errorMessage,
        submitOnboard,
        resetOnboard,
        isBusy: phase === "submitting" || phase === "provisioning" || phase === "telegram_confirm",
    };
}
